import { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useGame } from "@app/state/game.ts";
import { useTheme } from "@app/state/theme.ts";
import { HowTo } from "./HowTo.tsx";
import { CookieBanner } from "./CookieBanner.tsx";
import { useNetwork } from "@app/state/network.ts";

/** Pantalla sin conexión dentro del shell (la PWA abre aunque no haya red). */
function OfflineScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="screen offline" aria-labelledby="offline-title">
      <div className="offline__body">
        <svg viewBox="0 0 64 64" width="72" height="72" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <circle cx="32" cy="32" r="22" />
          <path d="M10 32h44M32 10c8 7 8 37 0 44M32 10c-8 7-8 37 0 44" opacity=".5" />
          <path d="M14 14l36 36" />
        </svg>
        <h1 id="offline-title" className="offline__title">Sin conexión</h1>
        <p className="offline__text">Sin conexión, vuelve a intentarlo. La ronda de hoy te espera en cuanto vuelvas a tener red.</p>
        <button type="button" className="cta" onClick={onRetry}>
          Reintentar
        </button>
      </div>
    </section>
  );
}

export function AppShell() {
  const [helpOpen, setHelpOpen] = useState(false);
  const round = useGame((s) => s.round);
  const load = useGame((s) => s.load);
  const online = useNetwork((s) => s.online);
  const resolved = useTheme((s) => s.resolved);
  const toggle = useTheme((s) => s.toggle);

  return (
    <>
      <div className="stadium" aria-hidden="true" />
      <main className="app">
        <header className="appbar">
          <div className="brand">
            <div className="logo" aria-label={`MapaGol${round ? `, ronda ${round.number}` : ""}`}>
              <span>
                Mapa<span className="logo__accent">Gol</span>
              </span>
              {round ? <span className="logo__round">#{round.number}</span> : null}
            </div>
            {__BETA__ ? (
              <span className="beta" title="Estamos en beta: el juego aún puede cambiar">
                BETA<span className="sr-only">: versión de prueba</span>
              </span>
            ) : null}
          </div>
          <div className="icons">
            <Link
              to="/estadisticas"
              className="ibtn"
              style={{ textDecoration: "none" }}
              aria-label="Estadísticas"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
              </svg>
            </Link>
            <button
              type="button"
              className="ibtn"
              onClick={toggle}
              aria-label={resolved === "light" ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4.5" />
                <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
              </svg>
            </button>
            <button
              type="button"
              className="ibtn"
              onClick={() => setHelpOpen(true)}
              aria-label="Cómo se juega"
            >
              ?
            </button>
          </div>
        </header>
        {!online && round ? (
          <p className="offline-banner" role="status">
            Sin conexión. Cuando vuelva la red podrás seguir donde lo dejaste.
          </p>
        ) : null}
        {!online && !round ? <OfflineScreen onRetry={() => void load()} /> : <Outlet />}
      </main>
      <HowTo open={helpOpen} onClose={() => setHelpOpen(false)} />
      <CookieBanner />
    </>
  );
}
